import { useMemo } from "react";
import { type LucideIcon } from "lucide-react";

import { FilterDefinitionType } from "@/components/@types";
import { Icon } from "@/components/atoms/Icon";
import { Popper } from "@/components/atoms/Popper";
import { FilterChipDefinitionList } from "@/components/organisms/UtilityBar/FilterChip/FilterChipDefinitionList";
import {
  CHIP_WORDS_CLASS,
  toTestId,
} from "@/components/organisms/UtilityBar/FilterChip/FilterChip.tools";
import {
  useFilterChipMutations,
  useFilterChipState,
} from "@/components/organisms/UtilityBar/FilterChip/FilterChipStateContext";
import { Button } from "@/components/ui/button";
import { ButtonGroupText } from "@/components/ui/button-group";
import { cn } from "@/components/ui/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export function FilterChipNameSection({
  isLocked,
  ReactIcon,
  filterDefinition,
  isNameOpen,
  setIsNameOpen,
  filterIndex,
  compact = false,
}: {
  isLocked: boolean;
  ReactIcon: LucideIcon;
  filterDefinition: FilterDefinitionType;
  isNameOpen: boolean;
  setIsNameOpen: (isOpen: boolean) => void;
  filterIndex: number;
  compact?: boolean;
}) {
  const { filters, filterDefinitions } = useFilterChipState();
  const { updateFilterAtIndex } = useFilterChipMutations();
  const availableFilterDefinitions = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(filterDefinitions).filter(
          ([key]) => !filters.some((filter) => filter.key === key),
        ),
      ),
    [filterDefinitions, filters],
  );

  const testId = `filter-chip-name-${toTestId(filterDefinition?.name)}`;
  const name = (
    <>
      {ReactIcon && <Icon ReactIcon={ReactIcon} />}
      {!compact && filterDefinition?.name}
    </>
  );

  // Locked, or with no other column to take its place: its name, as words.
  if (isLocked || Object.keys(availableFilterDefinitions).length === 0) {
    return (
      <ButtonGroupText
        data-testid={testId}
        className={cn(CHIP_WORDS_CLASS, "text-muted-foreground")}
      >
        {name}
      </ButtonGroupText>
    );
  }

  const button = (
    <Button
      variant="outline"
      size="sm"
      data-testid={testId}
      className="text-muted-foreground"
    >
      {name}
    </Button>
  );
  const popper = (
    <Popper
      anchorLayout="button-group"
      isOpenState={[isNameOpen, setIsNameOpen]}
      anchor={
        compact ? <TooltipTrigger asChild>{button}</TooltipTrigger> : button
      }
    >
      <FilterChipDefinitionList
        filterDefinitions={availableFilterDefinitions}
        onPick={(nextDefinition) => {
          updateFilterAtIndex(
            filterIndex,
            () =>
              ({
                key: nextDefinition.key,
                type: nextDefinition.type,
                operator: null,
                value: null,
              }) as any,
          );
        }}
        testIdPrefix="filter-name"
      />
    </Popper>
  );
  // Compact, it is its icon alone, and its name is the tooltip's.
  return compact ? (
    <Tooltip>
      {popper}
      <TooltipContent side="top">{filterDefinition?.name}</TooltipContent>
    </Tooltip>
  ) : (
    popper
  );
}
